/**
 * SidebarMessageList - Virtualized scrollable message list using react-window
 *
 * Scroll-position strategy when loading older messages (prepend):
 *  - scrollAnchorRef stores the ID of the first visible message at the moment
 *    the load is triggered (via onItemsRendered + handleScroll).
 *  - After messages grow at the front, we find that message's new index and call
 *    scrollToItem(newIndex, 'start') so it stays pinned to the top of the view.
 *
 * Row-height strategy:
 *  - rowHeights keyed by message ID (not index) so prepending never corrupts
 *    cached heights.
 *  - idxToIdRef (a plain ref) maps list-index → msgId and is updated sync in
 *    the messages effect so getRowHeight closure needs no messages dependency.
 */

import React, { memo, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { VariableSizeList as List } from 'react-window';
import { debounce } from 'lodash';
import { useUserDetailsContext } from 'context/userDetailsContext';
import SidebarMessage from './SidebarMessage';

const ESTIMATED_ROW_HEIGHT = 80;
const LOAD_MORE_HEIGHT = 40;
const SCROLL_TOP_THRESHOLD = 60;
const NEW_MSG_ANIM_MS = 1500;

const getMsgId = (msg) => msg?.id || msg?.sk || msg?.created_at;

const ListOuter = React.forwardRef(({ style, ...rest }, ref) => <div {...rest} ref={ref} style={{ ...style, overflowX: 'hidden' }} />);
ListOuter.displayName = 'ListOuter';

// ── Row renderer ──────────────────────────────────────────────────────────────
const RowRenderer = memo(({ index, style, data }) => {
  const { messages, hasLoadMoreRow, firmUserId, timezone, newMessageId, setRowHeight, isLoadingMore } = data;

  if (hasLoadMoreRow && index === 0) {
    return (
      <div style={style}>
        <div className="text-center py-2">
          {isLoadingMore && (
            <>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.875rem' }} />
              <span className="ms-2 text-muted small">Loading older messages...</span>
            </>
          )}
        </div>
      </div>
    );
  }

  const messageIdx = hasLoadMoreRow ? index - 1 : index;
  const message = messages[messageIdx];
  if (!message) return null;

  const msgId = getMsgId(message);
  const isOwnMessage = message?.author_details?.firm_user_id === firmUserId;
  const isNew = msgId === newMessageId;

  return (
    <div style={style}>
      <MeasuredRow rowId={msgId} listIndex={index} setRowHeight={setRowHeight}>
        <SidebarMessage message={message} isOwnMessage={isOwnMessage} timezone={timezone} isNew={isNew} />
      </MeasuredRow>
    </div>
  );
});
RowRenderer.displayName = 'RowRenderer';

// ── Main component ────────────────────────────────────────────────────────────
function SidebarMessageList({ scrollContainerRef, messages, totalCount, isLoading, hasMore, isLoadingMore, loadMore }) {
  const { userDetails, firmDetails } = useUserDetailsContext();
  const firmUserId = firmDetails?.firm_user_id;
  const timezone = userDetails?.timezone;

  const listRef = useRef(null);
  const outerRef = useRef(null);
  const containerRef = useRef(null);
  const observerRef = useRef(null);

  const rowHeights = useRef({}); // keyed by msgId
  const idxToIdRef = useRef([]); // index → msgId (updated before any resetAfterIndex)

  const [containerHeight, setContainerHeight] = useState(0);

  const hasScrolledToBottom = useRef(false);
  const lastMessageRef = useRef(null);
  const prevMsgCountRef = useRef(messages.length);

  const [newMessageId, setNewMessageId] = useState(null);
  const newMsgTimerRef = useRef(null);

  // ID of the first currently-visible message saved just before loadMore fires
  const scrollAnchorRef = useRef(null);
  // First visible list-index, kept current by onItemsRendered
  const firstVisibleIdxRef = useRef(0);

  const hasLoadMoreRow = hasMore || isLoadingMore;
  const itemCount = messages.length + (hasLoadMoreRow ? 1 : 0);

  // ── Row heights (ID-keyed) ────────────────────────────────────────────────
  const setRowHeight = useCallback((rowId, listIndex, height) => {
    if (!rowId || rowHeights.current[rowId] === height) return;
    rowHeights.current[rowId] = height;
    listRef.current?.resetAfterIndex(listIndex, false);
  }, []);

  // Stable closure — reads heights through idxToIdRef so messages is not a dep
  const getRowHeight = useCallback(
    (index) => {
      if (hasLoadMoreRow && index === 0) return LOAD_MORE_HEIGHT;
      const msgIdx = hasLoadMoreRow ? index - 1 : index;
      const msgId = idxToIdRef.current[msgIdx];
      return (msgId && rowHeights.current[msgId]) || ESTIMATED_ROW_HEIGHT;
    },
    [hasLoadMoreRow]
  );

  // ── ResizeObserver ────────────────────────────────────────────────────────
  const containerCallbackRef = useCallback((node) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    containerRef.current = node;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const h = Math.round(entries[0]?.contentRect?.height || 0);
      if (h > 0) {
        setContainerHeight((prev) => {
          if (prev === h) return prev;
          listRef.current?.resetAfterIndex(0, false);
          return h;
        });
      }
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // ── Central messages effect ───────────────────────────────────────────────
  useEffect(() => {
    const prev = prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    // Thread switch
    if (messages.length === 0) {
      rowHeights.current = {};
      idxToIdRef.current = [];
      hasScrolledToBottom.current = false;
      lastMessageRef.current = null;
      scrollAnchorRef.current = null;
      return;
    }

    // Sync mapping FIRST — getRowHeight reads it on every virtual row
    idxToIdRef.current = messages.map(getMsgId);

    const lastMsgId = getMsgId(messages[messages.length - 1]);

    // ── Older messages prepended (loadMore completed) ─────────────────────
    if (scrollAnchorRef.current && messages.length > prev) {
      // Tell react-window to re-query all item sizes with the updated id map
      listRef.current?.resetAfterIndex(0, false);

      const anchor = scrollAnchorRef.current;
      scrollAnchorRef.current = null;

      // After the re-layout paint, pin the anchor message to the top of the view
      requestAnimationFrame(() => {
        const anchorIdx = messages.findIndex((m) => getMsgId(m) === anchor);
        if (anchorIdx < 0) return;
        const listIndex = anchorIdx + (hasLoadMoreRow ? 1 : 0);
        listRef.current?.scrollToItem(listIndex, 'start');
      });

      lastMessageRef.current = lastMsgId;
      return;
    }

    // ── Initial load or new message appended ─────────────────────────────
    const isInitialLoad = !hasScrolledToBottom.current;
    const isNewMessageAtEnd = lastMessageRef.current !== null && lastMsgId !== lastMessageRef.current;

    if (isInitialLoad || isNewMessageAtEnd) {
      if (isNewMessageAtEnd) {
        setNewMessageId(lastMsgId);
        clearTimeout(newMsgTimerRef.current);
        newMsgTimerRef.current = setTimeout(() => setNewMessageId(null), NEW_MSG_ANIM_MS);
      }

      setTimeout(() => {
        requestAnimationFrame(() => listRef.current?.scrollToItem(itemCount - 1, 'end'));
        hasScrolledToBottom.current = true;
      }, 100);
    }

    lastMessageRef.current = lastMsgId;
  }, [messages, itemCount, hasLoadMoreRow]);

  useEffect(() => () => clearTimeout(newMsgTimerRef.current), []);

  // ── Load-more: triggered by scrolling near the top ────────────────────────
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const debouncedLoadMore = useRef(debounce(() => loadMoreRef.current(), 200)).current;

  useEffect(() => () => debouncedLoadMore.cancel(), [debouncedLoadMore]);

  const handleScroll = useCallback(
    ({ scrollOffset }) => {
      if (!hasScrolledToBottom.current) return;
      if (scrollOffset < SCROLL_TOP_THRESHOLD && hasMore && !isLoadingMore) {
        // Save the first visible message as the scroll anchor BEFORE firing load.
        // hasLoadMoreRow is already true here (hasMore===true), so row 0 is the
        // load-more indicator; first MESSAGE row starts at index 1.
        const firstVisIdx = firstVisibleIdxRef.current;
        const msgArrayIdx = hasLoadMoreRow ? Math.max(0, firstVisIdx - 1) : firstVisIdx;
        scrollAnchorRef.current = getMsgId(messages[Math.max(0, msgArrayIdx)]);
        debouncedLoadMore();
      }
    },
    [hasMore, isLoadingMore, debouncedLoadMore, messages, hasLoadMoreRow]
  );

  const handleItemsRendered = useCallback(({ visibleStartIndex }) => {
    firstVisibleIdxRef.current = visibleStartIndex;
  }, []);

  // ── Stable itemData ───────────────────────────────────────────────────────
  const itemData = useMemo(
    () => ({ messages, hasLoadMoreRow, firmUserId, timezone, newMessageId, setRowHeight, loadMore, isLoadingMore }),
    [messages, hasLoadMoreRow, firmUserId, timezone, newMessageId, setRowHeight, loadMore, isLoadingMore]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading && messages.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} />
      </div>
    );
  }

  if (!messages.length && !isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 text-muted">
        <p className="mb-0">No messages yet</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      <div ref={containerCallbackRef} className="flex-grow-1" style={{ minHeight: 0, overflowX: 'hidden' }}>
        {containerHeight > 0 && (
          <List
            ref={(node) => {
              listRef.current = node;
              if (scrollContainerRef) scrollContainerRef.current = node?._outerRef;
            }}
            outerRef={outerRef}
            outerElementType={ListOuter}
            height={containerHeight}
            itemCount={itemCount}
            itemSize={getRowHeight}
            itemData={itemData}
            width="100%"
            overscanCount={5}
            onScroll={handleScroll}
            onItemsRendered={handleItemsRendered}
            className="thin-scrollbar"
          >
            {RowRenderer}
          </List>
        )}
      </div>
    </div>
  );
}

// ── MeasuredRow ───────────────────────────────────────────────────────────────
const MeasuredRow = memo(({ rowId, listIndex, setRowHeight, children }) => {
  const rowRef = useRef(null);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(rowId, listIndex, rowRef.current.getBoundingClientRect().height);
    }
  }, [rowId, listIndex, setRowHeight, children]);

  return <div ref={rowRef}>{children}</div>;
});
MeasuredRow.displayName = 'MeasuredRow';

export default memo(SidebarMessageList);
